import BaseApiClient from "../api/baseApiClient.service";
import type { StudentKlausur as StudentKlausurDTO } from "../orgaAdmin/orgaAdmin.types";

export interface Klausur {
	id: string;
	title: string;
	index: number;
	studentKlausuren: StudentKlausurDTO[];
}

export type StudentKlausur = StudentKlausurDTO;

export interface CreateStudentKlausurResponse {
	message: string;
	studentKlausur: StudentKlausur;
}

/**
 * Service for managing Klausur operations for organization admins
 */
export class OrgaAdminKlausurService {
	private static instance: OrgaAdminKlausurService;

	private baseApi: BaseApiClient;

	// Base path mounted on the backend for klausur routes
	private baseApiSubPath = "/orga-admin/klausur";

	private constructor() {
		this.baseApi = BaseApiClient.getInstance();
	}

	public static getInstance(): OrgaAdminKlausurService {
		if (!OrgaAdminKlausurService.instance) {
			OrgaAdminKlausurService.instance = new OrgaAdminKlausurService();
		}
		return OrgaAdminKlausurService.instance;
	}

	/**
	 * Get all klausurs for an organization
	 * @param organizationId - The ID of the organization
	 * @returns Promise resolving to list of klausurs
	 */
	public async getKlausurs(organizationId: string): Promise<Klausur[]> {
		return this.baseApi.get<Klausur[]>(
			`${this.baseApiSubPath}/${organizationId}/get-all-klausuren`,
		);
	}

	/**
	 * Create a new student klausur with PDF upload
	 * @param organizationId - The ID of the organization
	 * @param klausurId - The ID of the klausur to create student klausur for
	 * @param file - The PDF file to upload
	 * @param internalIdentifier - Internal identifier for the student klausur
	 * @param gruppeId - The ID of the group to assign the student klausur to
	 * @returns Promise resolving to the created student klausur
	 */
	public async createStudentKlausur(
		organizationId: string,
		klausurId: string,
		file: File,
		internalIdentifier: string,
		gruppeId: string,
	): Promise<CreateStudentKlausurResponse> {
		// Validate file type
		if (file.type !== "application/pdf") {
			throw new Error("Only PDF files are allowed");
		}

		return this.baseApi.uploadFile<CreateStudentKlausurResponse>(
			`${this.baseApiSubPath}/${organizationId}/klausur/${klausurId}/student-klausur/create`,
			file,
			{ internalIdentifier, gruppeId },
		);
	}

	/**
	 * Delete a student klausur
	 * @param organizationId - The ID of the organization
	 * @param studentKlausurId - The ID of the student klausur to delete
	 * @returns Promise resolving when deleted
	 */
	public async deleteStudentKlausur(
		organizationId: string,
		studentKlausurId: string,
	): Promise<void> {
		return this.baseApi.delete(
			`${this.baseApiSubPath}/${organizationId}/student-klausur/${studentKlausurId}`,
		);
	}

	/**
	 * Update the gruppe (ausschuss) of a student klausur
	 * @param organizationId - The ID of the organization
	 * @param studentKlausurId - The ID of the student klausur to update
	 * @param gruppeId - The new gruppe ID
	 * @returns Promise resolving to the updated student klausur
	 */
	public async updateStudentKlausurGruppe(
		organizationId: string,
		studentKlausurId: string,
		gruppeId: string,
	): Promise<StudentKlausur> {
		return this.baseApi.patch<StudentKlausur, { gruppeId: string }>(
			`${this.baseApiSubPath}/${organizationId}/student-klausur/${studentKlausurId}/gruppe`,
			{ gruppeId },
		);
	}
}

export default OrgaAdminKlausurService;
